import type { ScreenParameters, SpecificNavigationProp } from 'app/types/navigation';
import type { Listing } from 'app/types/listing';
import type { User } from 'app/types/user';
import { type Channel } from 'app/types/chat';
import { Platform } from 'react-native';
import type { Parameters as BillboardParameters } from './navigator';

function isBillboardNavigator<T extends keyof ScreenParameters>(navigation: SpecificNavigationProp<T>) {
  return (
    navigation.getState().routeNames.includes('landing-page') &&
    navigation.getState().routeNames.includes('all-listings')
  );
}

export function navigateToAllListings<T extends keyof ScreenParameters>(
  navigation: SpecificNavigationProp<T>,
  params?: BillboardParameters['all-listings'],
  mode: 'navigate' | 'replace' = 'navigate',
) {
  if (isBillboardNavigator(navigation)) {
    if (mode === 'replace') {
      navigation.replace('all-listings', params);
    } else {
      navigation.navigate('all-listings', params, { pop: true });
    }
    return;
  }

  if (navigation.getState().routeNames.includes('billboard')) {
    navigation.navigate('billboard', {
      screen: 'all-listings',
      params,
    });
  } else {
    navigation.navigate(
      'main',
      {
        screen: 'billboard',
        params: { screen: 'all-listings', params },
      },
      { pop: true },
    );
  }
}

export function navigateToLandingPage<T extends keyof ScreenParameters>(
  navigation: SpecificNavigationProp<T>,
  mode: 'navigate' | 'replace' = 'navigate',
) {
  if (isBillboardNavigator(navigation)) {
    if (mode === 'replace') {
      navigation.replace('landing-page');
    } else {
      navigation.navigate('landing-page', undefined, { pop: true });
    }
    return;
  }

  if (navigation.getState().routeNames.includes('billboard')) {
    navigation.navigate('billboard', {
      screen: 'landing-page',
    });
  } else {
    navigation.navigate(
      'main',
      {
        screen: 'billboard',
        params: { screen: 'landing-page' },
      },
      { pop: true },
    );
  }
}

export function navigateToListing<T extends keyof ScreenParameters>(
  navigation: SpecificNavigationProp<T>,
  listingId: Listing['id'],
  isGroupedListing?: boolean,
  fromNewScreen?: boolean,
  hideApproximateLocation?: boolean,
) {
  const currentRoute = navigation.getState().routes[navigation.getState().index];
  const currentParams = currentRoute?.params as BillboardParameters['all-listings'] | undefined;
  const guestLocationParams =
    Platform.OS === 'web'
      ? {
          searchCenterLatitude: currentParams?.searchCenterLatitude,
          searchCenterLongitude: currentParams?.searchCenterLongitude,
          searchDistance: currentParams?.searchDistance,
        }
      : {};

  if (isGroupedListing) {
    if (navigation.getState().routeNames.includes('grouped-listing')) {
      navigation.navigate('grouped-listing', { id: listingId, ...guestLocationParams }, { pop: true });
    } else {
      navigation.navigate(
        'listingModal',
        {
          screen: 'grouped-listing',
          params: { id: listingId, ...guestLocationParams },
        },
        { pop: true },
      );
    }
    return;
  }

  // hideApproximateLocation is true when we navigate to a listing via the map
  if (Platform.OS !== 'web' && hideApproximateLocation && isBillboardNavigator(navigation)) {
    navigation.navigate(
      'listingModal',
      {
        screen: 'one-listing',
        params: { id: listingId, fromNewScreen, hideApproximateLocation },
      },
      { pop: true },
    );
    return;
  }

  navigateToListingWithParams(navigation, {
    id: listingId,
    fromNewScreen,
    hideApproximateLocation,
    ...guestLocationParams,
  });
}

export function navigateToListingWithParams<T extends keyof ScreenParameters>(
  navigation: SpecificNavigationProp<T>,
  params: BillboardParameters['one-listing'],
) {
  if (navigation.getState().routeNames.includes('one-listing')) {
    navigation.navigate('one-listing', params, { pop: true });
  } else {
    navigation.navigate('listingModal', { screen: 'one-listing', params }, { pop: true });
  }
}

export function navigateToUserProfile<T extends keyof ScreenParameters>(
  navigation: SpecificNavigationProp<T>,
  userId: User['id'],
  listingId?: Listing['id'],
  listingType?: Listing['type'],
  channelURL?: Channel['channel_url'],
) {
  if (navigation.getState().routeNames.includes('neighbour-profile')) {
    navigation.navigate('neighbour-profile', { userId, listingId, listingType, channelURL }, { pop: true });
  } else {
    navigation.navigate(
      'userProfileModal',
      {
        screen: 'neighbour-profile',
        params: { userId, listingId, listingType, channelURL },
      },
      { pop: true },
    );
  }
}

export function navigateToMyInformation<T extends keyof ScreenParameters>(navigation: SpecificNavigationProp<T>) {
  if (navigation.getState().routeNames.includes('my-information')) {
    navigation.navigate('profile', { screen: 'my-information' });
  } else {
    navigation.navigate(
      'userProfileModal',
      {
        screen: 'my-information',
      },
      { pop: true },
    );
  }
}
